import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Button, Label, DateInput, Input, Textarea, RadioGroup } from '../../../components/ui';
import { createTicketingRequest, uploadTicketingRequestAttachment } from '../ticketingApi';
import { TICKETING_FORM_KEY } from '../ticketingFormKeys';
import { TicketingAttachmentsInput } from '../components/TicketingAttachmentsInput';

const IT_CATEGORY_OPTIONS = [
  { id: 'hardware', label: 'Hardware' },
  { id: 'software', label: 'Software' },
  { id: 'other', label: 'Other' },
];

const URGENCY_OPTIONS = [
  { id: 'low', label: 'Low' },
  { id: 'normal', label: 'Normal' },
  { id: 'high', label: 'High' },
  { id: 'critical', label: 'Critical' },
];

const IMPACT_OPTIONS = [
  { id: 'all_company', label: 'All company' },
  { id: 'my_department', label: 'My department' },
  { id: 'only_me', label: 'Only me' },
];

const todayYmdLocal = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const itCategoryRadioOptions = IT_CATEGORY_OPTIONS.map((o) => ({ value: o.id, label: o.label }));
const urgencyRadioOptions = URGENCY_OPTIONS.map((o) => ({ value: o.id, label: o.label }));
const impactRadioOptions = IMPACT_OPTIONS.map((o) => ({ value: o.id, label: o.label }));

export function GeneralItTicketRequestForm({ department, category, deptPath, navigate, onSuccess }) {
  const { theme } = useTheme();
  const [ticketDate, setTicketDate] = useState(todayYmdLocal);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [itCategory, setItCategory] = useState('');
  const [urgency, setUrgency] = useState('normal');
  const [impact, setImpact] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!department || !category) return;
    const next = {};
    if (!subject.trim()) next.subject = 'Enter a subject.';
    if (!description.trim()) next.description = 'Enter a short description.';
    if (!itCategory) next.itCategory = 'Select hardware, software, or other.';
    if (!impact) next.impact = 'Select who is affected.';
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    setErrors({});
    const payload = {
      description: description.trim(),
      itCategory,
      urgency,
      impact,
    };
    setSubmitting(true);
    try {
      const created = await createTicketingRequest({
        ticketingDepartmentId: department.id,
        ticketingCategoryId: category.id,
        formKey: TICKETING_FORM_KEY.GENERAL_IT,
        ticketDate,
        subject,
        payload,
      });
      if (Array.isArray(attachments) && attachments.length > 0 && created?.id != null) {
        await Promise.all(
          attachments.map((file) =>
            uploadTicketingRequestAttachment(created.id, file, null),
          ),
        );
      }
      onSuccess(created);
    } catch (err) {
      alert(err?.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'rounded-2xl border shadow-sm overflow-hidden',
        theme.border.light,
        theme.colors.surface
      )}
    >
      <div className="h-1 bg-gradient-to-r from-blue-500 to-indigo-600" />
      <div className="p-4 md:p-5 space-y-6">
        <section>
          <Label htmlFor="git-ticket-date" className="mb-2 block font-semibold">
            Ticket date
          </Label>
          <div className="max-w-xs">
            <DateInput
              id="git-ticket-date"
              name="ticketDate"
              value={ticketDate}
              onChange={(e) => setTicketDate(e.target.value)}
              showClearButton={false}
              showFormatHint={false}
            />
          </div>
          <p className={cn('text-xs mt-2', theme.text.muted)}>
            Status and requester are set automatically when you submit.
          </p>
        </section>

        <section>
          <Label htmlFor="git-subject" className="mb-2 block font-semibold">
            Subject
          </Label>
          <Input
            id="git-subject"
            type="text"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setErrors((er) => ({ ...er, subject: undefined }));
            }}
            placeholder="Short subject for this request"
            error={!!errors.subject}
            className="rounded-xl"
          />
          {errors.subject ? (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">{errors.subject}</p>
          ) : null}
        </section>

        <section>
          <Label htmlFor="git-description" className="mb-2 block font-semibold">
            Description
          </Label>
          <Textarea
            id="git-description"
            rows={4}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setErrors((er) => ({ ...er, description: undefined }));
            }}
            placeholder="What IT issue or request is this about?"
            error={!!errors.description}
            className="rounded-xl px-4 py-3 min-h-[100px]"
          />
          {errors.description ? (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">{errors.description}</p>
          ) : null}
        </section>

        <section>
          <h2 className={cn('text-base font-semibold mb-2', theme.text.primary)}>Category</h2>
          <p className={cn('text-sm mb-4', theme.text.muted)}>Hardware, software, or other.</p>
          <RadioGroup
            name="itCategory"
            options={itCategoryRadioOptions}
            value={itCategory}
            onChange={(v) => {
              setItCategory(v);
              setErrors((er) => ({ ...er, itCategory: undefined }));
            }}
            variant="card"
            orientation="horizontal"
            className="flex-wrap gap-3"
          />
          {errors.itCategory ? (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">{errors.itCategory}</p>
          ) : null}
        </section>

        <section>
          <h2 className={cn('text-base font-semibold mb-4', theme.text.primary)}>Urgency</h2>
          <RadioGroup
            name="urgency"
            options={urgencyRadioOptions}
            value={urgency}
            onChange={setUrgency}
            variant="card"
            orientation="horizontal"
            className="flex-wrap gap-3"
          />
        </section>

        <section>
          <h2 className={cn('text-base font-semibold mb-2', theme.text.primary)}>Impact</h2>
          <p className={cn('text-sm mb-4', theme.text.muted)}>Who is affected?</p>
          <RadioGroup
            name="impact"
            options={impactRadioOptions}
            value={impact}
            onChange={(v) => {
              setImpact(v);
              setErrors((er) => ({ ...er, impact: undefined }));
            }}
            variant="card"
            orientation="horizontal"
            className="flex-wrap gap-3"
          />
          {errors.impact ? (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">{errors.impact}</p>
          ) : null}
        </section>

        <TicketingAttachmentsInput
          id="git-attachments"
          files={attachments}
          onChange={setAttachments}
          theme={theme}
        />

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit IT request'}
          </Button>
          <Button type="button" variant="secondary" disabled={submitting} onClick={() => navigate(deptPath)}>
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
}
